/**
 * Date Formatting Utilities with i18n Support
 * 
 * Provides locale-aware date formatting using date-fns.
 * Automatically uses the current i18n language for formatting.
 */

import { format as dateFnsFormat, formatRelative, formatDistance, Locale } from 'date-fns';
import { es, enGB } from 'date-fns/locale';
import i18n from '@/core/i18n/i18n';

/**
 * Map of supported language codes to date-fns locales.
 *
 * We intentionally use `enGB` for English (not `enUS`) so that locale-aware
 * date-fns tokens (`P`, `PP`, `PPP`, `PPp`) render day-first (DD/MM/YYYY) —
 * the European convention Shaka operates under. This gives "30 Dec 2025" /
 * "30/12/2025" in English and "30 dic 2025" / "30/12/2025" in Spanish, with
 * consistent ordering regardless of the UI language.
 */
const localeMap: Record<string, Locale> = {
  en: enGB,
  es: es,
};

/**
 * Get the current date-fns locale based on i18n language
 */
export function getDateLocale(): Locale {
  const currentLang = i18n.language?.split('-')[0] || 'en';
  return localeMap[currentLang] || enGB;
}

/**
 * Format a date with locale support
 * 
 * @param date - The date to format
 * @param formatStr - The format string (date-fns format)
 * @param options - Optional formatting options
 * @returns Formatted date string
 * 
 * @example
 * formatDate(new Date(), 'dd MMMM yyyy') // "30 December 2025" (en) / "30 diciembre 2025" (es)
 * formatDate(new Date(), 'dd MMM') // "30 Dec" (en) / "30 dic" (es)
 */
export function formatDate(
  date: Date | number | string | null | undefined,
  formatStr: string,
  options?: { locale?: Locale }
): string {
  if (date == null) return '—';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (dateObj instanceof Date && isNaN(dateObj.getTime())) return '—';
  if (typeof dateObj === 'number' && isNaN(dateObj)) return '—';
  const locale = options?.locale || getDateLocale();
  
  return dateFnsFormat(dateObj, formatStr, { locale });
}

/**
 * Format a date relative to now (e.g., "2 days ago", "in 3 hours")
 * 
 * @param date - The date to format
 * @param baseDate - The base date to compare to (default: now)
 * @returns Relative date string
 * 
 * @example
 * formatRelativeDate(new Date('2025-12-28')) // "2 days ago" / "hace 2 días"
 */
export function formatRelativeDate(
  date: Date | number | string,
  baseDate: Date = new Date()
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const locale = getDateLocale();
  
  return formatRelative(dateObj, baseDate, { locale });
}

/**
 * Format the distance between two dates (e.g., "about 2 hours", "3 months")
 * 
 * @param date - The date to compare
 * @param baseDate - The base date to compare to (default: now)
 * @param options - Formatting options
 * @returns Distance string
 * 
 * @example
 * formatDistanceDate(new Date('2025-12-28')) // "2 days" / "2 días"
 */
export function formatDistanceDate(
  date: Date | number | string,
  baseDate: Date = new Date(),
  options?: { addSuffix?: boolean; includeSeconds?: boolean }
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const locale = getDateLocale();
  
  return formatDistance(dateObj, baseDate, { 
    locale,
    addSuffix: options?.addSuffix ?? false,
    includeSeconds: options?.includeSeconds ?? false,
  });
}

/**
 * Common date format patterns
 */
export const DATE_FORMATS = {
  /** December 30, 2025 / 30 de diciembre de 2025 */
  FULL_DATE: 'PPP',
  /** Dec 30, 2025 / 30 dic 2025 */
  MEDIUM_DATE: 'PP',
  /** 12/30/2025 / 30/12/2025 */
  SHORT_DATE: 'P',
  /** December 2025 / diciembre 2025 */
  MONTH_YEAR: 'MMMM yyyy',
  /** Dec 2025 / dic 2025 */
  SHORT_MONTH_YEAR: 'MMM yyyy',
  /** 30 December / 30 de diciembre */
  MONTH_DAY: 'dd MMMM',
  /** 30 Dec / 30 dic */
  SHORT_MONTH_DAY: 'dd MMM',
  /** Monday, 30 December / lunes, 30 de diciembre */
  WEEKDAY_DATE: 'EEEE, dd MMMM',
  /** 10:30 AM / 10:30 */
  TIME: 'p',
  /** December 30, 2025 at 10:30 AM */
  DATETIME: 'PPp',
} as const;

export type DateFormatKey = keyof typeof DATE_FORMATS;

/**
 * Format a Date as a local YYYY-MM-DD string without UTC conversion.
 *
 * `Date.toISOString()` converts to UTC first, which can shift the date
 * backwards or forwards depending on the user's timezone. This helper
 * reads the LOCAL year/month/day to avoid that bug.
 *
 * @example
 * // User in CET (UTC+1), date is 2026-01-15 at 23:30 local
 * new Date('2026-01-15T23:30:00+01:00').toISOString().split('T')[0]
 * // → '2026-01-16' ← WRONG (shifted to UTC)
 * formatLocalDate(new Date('2026-01-15T23:30:00+01:00'))
 * // → '2026-01-15' ← CORRECT (local date)
 */
export function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * True when the value falls on a LOCAL calendar day after today.
 *
 * Operational surfaces ask "has this day arrived yet?" (is the guest due, is
 * the band paired ahead of arrival). Comparing instants answers the wrong
 * question — a stay arriving today at 00:00 is not "in the future" once the
 * clock passes midnight. Compares local day starts, like `formatLocalDate`.
 */
export function isFutureDay(value: Date | string | null | undefined): boolean {
  if (!value) return false;
  const day = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(day.getTime())) return false;
  return formatLocalDate(day) > formatLocalDate(new Date());
}
